/**
 * Multiclass spell slot calculation — D&D 5e SRD rules.
 *
 * Provides caster type mapping, caster level calculation, and
 * the multiclass spell slots table (PHB p.165).
 */

import type { ClassEntry } from '../../types.js';
import type { SrdClass } from './srd/types.js';
import { getClassLevelData } from './srd/class-levels.js';
import type { SpellSlotsData, SpellSlotLevel, PactMagicSlot } from './types.js';

export type CasterType = 'full' | 'half' | 'third' | 'pact' | 'none';

/** Maps class/subclass slug → caster type per SRD. */
export const CLASS_CASTER_TYPE: Record<string, CasterType> = {
  bard: 'full',
  cleric: 'full',
  druid: 'full',
  sorcerer: 'full',
  wizard: 'full',
  paladin: 'half',
  ranger: 'half',
  'eldritch-knight': 'third',
  'arcane-trickster': 'third',
  warlock: 'pact',
};

/**
 * Calculate effective caster level for multiclass spell slot lookup.
 *
 * - Full casters contribute level × 1
 * - Half casters contribute floor(level / 2)
 * - Third casters contribute floor(level / 3)
 * - Pact magic (warlock) is tracked separately, not added to caster level
 *
 * Subclass slugs are checked when the class slug itself is not a caster
 * (e.g. Fighter with 'eldritch-knight' subclass).
 */
export function calculateCasterLevel(classData: ClassEntry[]): {
  casterLevel: number;
  hasPactMagic: boolean;
} {
  let sum = 0;
  let hasPactMagic = false;

  for (const entry of classData) {
    const type =
      CLASS_CASTER_TYPE[entry.classSlug] ??
      CLASS_CASTER_TYPE[entry.subclassSlug ?? ''] ??
      'none';

    switch (type) {
      case 'full':
        sum += entry.level;
        break;
      case 'half':
        sum += Math.floor(entry.level / 2);
        break;
      case 'third':
        sum += Math.floor(entry.level / 3);
        break;
      case 'pact':
        hasPactMagic = true;
        break;
      // 'none' — no contribution
    }
  }

  return { casterLevel: sum, hasPactMagic };
}

/**
 * Multiclass spell slots table — D&D 5e SRD (PHB p.165).
 *
 * Key = effective caster level (1-20).
 * Value = { spell level → number of slots }.
 */
export const MULTICLASS_SPELL_SLOTS: Record<number, Record<number, number>> = {
  1:  { 1: 2 },
  2:  { 1: 3 },
  3:  { 1: 4, 2: 2 },
  4:  { 1: 4, 2: 3 },
  5:  { 1: 4, 2: 3, 3: 2 },
  6:  { 1: 4, 2: 3, 3: 3 },
  7:  { 1: 4, 2: 3, 3: 3, 4: 1 },
  8:  { 1: 4, 2: 3, 3: 3, 4: 2 },
  9:  { 1: 4, 2: 3, 3: 3, 4: 3, 5: 1 },
  10: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2 },
  11: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },
  12: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },
  13: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },
  14: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },
  15: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },
  16: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },
  17: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1, 9: 1 },
  18: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 1, 7: 1, 8: 1, 9: 1 },
  19: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 1, 8: 1, 9: 1 },
  20: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 2, 8: 1, 9: 1 },
};

/**
 * Warlock Pact Magic progression (PHB Warlock table). Pact slots are a SEPARATE
 * resource from normal spell slots — all the same level, recharge on a short
 * rest. Maps warlock level → { count of pact slots, their spell level }.
 */
export const WARLOCK_PACT_SLOTS: Record<number, { count: number; level: number }> = {
  1: { count: 1, level: 1 }, 2: { count: 2, level: 1 }, 3: { count: 2, level: 2 },
  4: { count: 2, level: 2 }, 5: { count: 2, level: 3 }, 6: { count: 2, level: 3 },
  7: { count: 2, level: 4 }, 8: { count: 2, level: 4 }, 9: { count: 2, level: 5 },
  10: { count: 2, level: 5 }, 11: { count: 3, level: 5 }, 12: { count: 3, level: 5 },
  13: { count: 3, level: 5 }, 14: { count: 3, level: 5 }, 15: { count: 3, level: 5 },
  16: { count: 3, level: 5 }, 17: { count: 4, level: 5 }, 18: { count: 4, level: 5 },
  19: { count: 4, level: 5 }, 20: { count: 4, level: 5 },
};

function clampClassLevel(level: number): number {
  return Math.max(1, Math.min(20, Math.floor(Number.isFinite(level) ? level : 1)));
}

/**
 * Deterministic spell-slot maxima for a SINGLE class at a given level. Returns
 * fresh slots (current === max); to update a LIVE sheet without losing spent
 * slots, merge via `mergeSpellSlots`. Warlock → pact magic; other casters →
 * normal slots from the SRD class table. Returns null for non-casters.
 */
export function computeSpellSlots(srdClass: SrdClass, level: number): SpellSlotsData | null {
  if (!srdClass.spellcastingAbility) return null;
  const lvl = clampClassLevel(level);
  const casterType = CLASS_CASTER_TYPE[srdClass.slug] ?? 'none';
  if (casterType === 'pact') {
    const pact = WARLOCK_PACT_SLOTS[lvl];
    return { slots: {}, pact: pact ? { level: pact.level, max: pact.count, current: pact.count } : null };
  }
  const levelData = getClassLevelData(srdClass, lvl);
  const slots: Record<number, SpellSlotLevel> = {};
  if (levelData?.spellcasting?.slots) {
    for (const [slotLvl, max] of Object.entries(levelData.spellcasting.slots)) {
      const n = Number(slotLvl);
      if (max > 0) slots[n] = { max, current: max };
    }
  }
  return { slots, pact: null };
}

/**
 * Merge freshly-computed `target` maxima into an `existing` SpellSlotsData,
 * PRESERVING spent `current`: each level keeps its current and only credits
 * newly-gained slots; levels no longer granted are dropped. Pact: same tier
 * preserves current (+ gained); a tier change resets to full (RAW — old-tier
 * slots are replaced).
 */
export function mergeSpellSlots(
  existing: SpellSlotsData | null | undefined,
  target: SpellSlotsData | null,
): SpellSlotsData | null {
  if (!target) return null;
  const prev = existing ?? { slots: {}, pact: null };
  const slots: Record<number, SpellSlotLevel> = {};
  for (const [lvlStr, t] of Object.entries(target.slots)) {
    const lvl = Number(lvlStr);
    const e = prev.slots[lvl];
    if (e) {
      const gained = Math.max(0, t.max - e.max);
      slots[lvl] = { max: t.max, current: Math.min(e.current + gained, t.max) };
    } else {
      slots[lvl] = { max: t.max, current: t.max };
    }
  }
  let pact: PactMagicSlot | null = null;
  if (target.pact) {
    const e = prev.pact;
    if (e && e.level === target.pact.level) {
      const gained = Math.max(0, target.pact.max - e.max);
      pact = { level: target.pact.level, max: target.pact.max, current: Math.min(e.current + gained, target.pact.max) };
    } else {
      pact = { ...target.pact };
    }
  }
  return { slots, pact };
}
